import { useState } from 'react';
import classNames from 'classnames';

import type { NativeWistiaPlayer } from '../../../HomepageRedesign/components/ExtendedVideoSection/types';
import type {
  ExtendedButtonVariant,
  VideoSectionProps,
} from '../../../HomepageRedesign/components';
import {
  ExtendedButton,
  ExtendedVideoSection,
} from '../../../HomepageRedesign/components';
import styles from './VideoTriggerButton.module.css';
import type { ReviewsRedesignSections } from 'src/pages/HomepageRedesign/components/CtaTrackerV2';
import { CtaTrackerV2 } from 'src/pages/HomepageRedesign/components/CtaTrackerV2';

interface VideoTriggerButtonProps extends VideoSectionProps {
  ctaText: string;
  variant?: ExtendedButtonVariant;
  ctaStyles?: string;
}

/**
 * This component triggers a video to play within a popover when the button is clicked,
 * keeping the video preview hidden from the user until interaction occurs.
 */
export default function VideoTriggerButton(props: VideoTriggerButtonProps) {
  const [player, setPlayer] = useState<NativeWistiaPlayer | null>(null);

  const onClickHandler = () => {
    player?.play();
  };

  const {
    ctaText,
    variant = 'solid-kale-2',
    wistiaOptions,
    className,
    ctaStyles,
    moduleName,
    ...restProps
  } = props;

  return (
    <>
      <CtaTrackerV2
        type="select"
        moduleLevel={1}
        moduleName={moduleName as ReviewsRedesignSections}
      >
        <ExtendedButton
          onClick={onClickHandler}
          className={ctaStyles}
          variant={variant}
          aria-label="Play video"
        >
          {ctaText}
        </ExtendedButton>
      </CtaTrackerV2>
      <ExtendedVideoSection
        wistiaOptions={{
          ...wistiaOptions,
          popover: true,
          // Prevent utilizing native IOS video player
          playsinline: true,
          popoverPreventScroll: true,
        }}
        className={classNames(styles.hiddenVideoPlayer, className)}
        onReady={(api: NativeWistiaPlayer) => setPlayer(api)}
        aria-hidden="true"
        data-testid="videoTriggerButtonSection"
        moduleName={moduleName}
        {...restProps}
      />
    </>
  );
}
